/**
 * PATH: packages/ui/src/kanban/components/card-detail/AiActionsBar.tsx
 * FILE_ID: UI-KANBAN-CARDDETAIL-AIACTIONSBAR-001
 * PURPOSE: On-demand "Structure with AI" bar under the card description.
 *          A single click sends the note to the co-located local LLM
 *          (zero API cost) which returns structured, confirm-to-apply
 *          actions: a follow-up (sets followup_date), a task (sets
 *          due_date), or a checklist step — each with an optional person.
 * WHY:     Operator (Bravada, 2026-06-17): "I need a follow up with Tim on
 *          Thursday … this is a action sequence that should be on the
 *          action lane … tim is the person it should auto suggest." The
 *          free heuristic chips (DescriptionActionsBar) can't reinterpret
 *          "follow up … Thursday" as a follow-up with a person; the local
 *          model can. On-demand + confirm keeps false matches cheap.
 * FLOW:
 *   text → [Structure with AI] → api.parseCardActions → suggestion chips
 *        → confirm → attachAssignee / updateCard(followup_date|due_date)
 *                    / createChecklistItem
 * DEPENDENCIES:
 *   - ../../api KanbanApi.parseCardActions + CardActionSuggestion
 *   - ./descriptionActions: resolveMemberToken + the heuristic gate
 *   - @tanstack/react-query: checklist cache invalidation on step add
 * RELATED:
 *   - DescriptionActionsBar.tsx: the free heuristic sibling (always on)
 *   - CardDetailDrawer.tsx: mounts this beside DescriptionActionsBar
 */

import { useMemo, useState, type ReactNode } from 'react'
import type { QueryClient } from '@tanstack/react-query'
import {
  Bell,
  CalendarClock,
  Check,
  CheckSquare,
  Loader2,
  Sparkles,
  UserPlus,
  X,
} from 'lucide-react'
import type { CardActionSuggestion, KanbanApi, KanbanMember } from '../../api'
import type { Card } from '../../types'
import { looksActionable, resolveMemberToken } from './descriptionActions'

export interface AiActionsBarProps {
  /** The live description text (defaults server-side to the saved summary
   *  when empty, but we gate the button on having something to parse). */
  text: string
  card: Card
  api: KanbanApi
  qc: QueryClient
  members?: KanbanMember[]
  /** Bubble the updated card up so the drawer repaints assignees / dates. */
  onChanged?: (card: Card) => void
  /** Mirror an applied due date into the drawer's date control. */
  onDueApplied?: (iso: string) => void
  /** Mirror an applied follow-up date into the drawer (when it has one). */
  onFollowupApplied?: (iso: string) => void
}

type Phase = 'idle' | 'loading' | 'done'

const KIND_ICON: Record<CardActionSuggestion['kind'], ReactNode> = {
  follow_up: <Bell className="h-3 w-3" aria-hidden />,
  task: <CalendarClock className="h-3 w-3" aria-hidden />,
  checklist_step: <CheckSquare className="h-3 w-3" aria-hidden />,
}

/** What applying a suggestion will actually do — used to disable the
 *  Apply button for an informational-only suggestion (nothing to set). */
function resolveEffect(
  action: CardActionSuggestion,
  members?: KanbanMember[],
): { userId: number | null; hasEffect: boolean } {
  let userId = action.person_user_id
  if (userId == null && action.person_name && members && members.length > 0) {
    userId = resolveMemberToken(action.person_name, members)?.id ?? null
  }
  const hasEffect =
    userId != null || !!action.date || action.kind === 'checklist_step'
  return { userId, hasEffect }
}

export function AiActionsBar({
  text,
  card,
  api,
  qc,
  members,
  onChanged,
  onDueApplied,
  onFollowupApplied,
}: AiActionsBarProps) {
  const [phase, setPhase] = useState<Phase>('idle')
  const [unavailable, setUnavailable] = useState(false)
  const [suggestions, setSuggestions] = useState<CardActionSuggestion[]>([])
  const [appliedIdx, setAppliedIdx] = useState<Set<number>>(() => new Set())
  const [busyIdx, setBusyIdx] = useState<number | null>(null)

  const emphasise = useMemo(() => looksActionable(text, members), [text, members])

  // parseCardActions is optional on the interface (legacy hosts may omit
  // it) — hide the whole bar when the host didn't wire it. (After all
  // hooks so hook order stays stable.)
  if (typeof api.parseCardActions !== 'function') return null

  const trimmed = (text ?? '').trim()
  const canParse = trimmed.length >= 4

  const runParse = async () => {
    if (!canParse || phase === 'loading') return
    setPhase('loading')
    setUnavailable(false)
    setAppliedIdx(new Set())
    try {
      const res = await api.parseCardActions!(card.id, trimmed)
      setUnavailable(!res.available)
      setSuggestions(res.available && res.is_action ? res.actions : [])
    } catch {
      // parseCardActions resolves on transport failure, but stay defensive.
      setUnavailable(true)
      setSuggestions([])
    } finally {
      setPhase('done')
    }
  }

  const markApplied = (idx: number) =>
    setAppliedIdx((prev) => {
      const next = new Set(prev)
      next.add(idx)
      return next
    })

  const applyAction = async (action: CardActionSuggestion, idx: number) => {
    if (busyIdx != null) return
    const { userId } = resolveEffect(action, members)
    setBusyIdx(idx)
    try {
      let updated: Card | null = null
      if (userId != null) {
        updated = await api.attachAssignee(card.id, userId)
      }
      if (action.date && action.kind !== 'checklist_step') {
        // Prefer action_date for task/step dates; follow_up keeps its field.
        const patch =
          action.kind === 'follow_up'
            ? { followup_date: action.date }
            : { action_date: action.date }
        updated = await api.updateCard(card.id, patch as never)
        if (action.kind === 'follow_up') onFollowupApplied?.(action.date)
        else onDueApplied?.(action.date)
      }
      if (action.kind === 'checklist_step') {
        const lists = await api.listChecklists(card.id)
        const target = lists[0] ?? (await api.createChecklist(card.id, 'Steps'))
        await api.createChecklistItem({ checklist: target.id, title: action.title })
        void qc.invalidateQueries({
          queryKey: ['kanban', 'card', card.id, 'checklists'],
        })
        void qc.invalidateQueries({ queryKey: ['kanban', 'card', card.id] })
      }
      if (updated) onChanged?.(updated)
      markApplied(idx)
    } catch {
      // The scoped fetch layer dispatches `quotech.kanban.error`, toasted
      // by the host. Leave the chip so the operator can retry.
    } finally {
      setBusyIdx(null)
    }
  }

  const pending = suggestions
    .map((action, idx) => ({ action, idx }))
    .filter(({ idx }) => !appliedIdx.has(idx))

  const buttonLabel = phase === 'loading' ? 'Structuring…' : 'Structure with AI'

  return (
    <div className="mt-1.5" data-card-ai-actions="">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => void runParse()}
          disabled={!canParse || phase === 'loading'}
          title={
            canParse
              ? 'Use AI to turn this note into a follow-up / task / steps'
              : 'Type a note first'
          }
          className={[
            'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-colors disabled:opacity-50',
            emphasise
              ? 'border-violet-300 bg-violet-50 text-violet-700 hover:bg-violet-100 dark:border-violet-800/60 dark:bg-violet-950/40 dark:text-violet-300 dark:hover:bg-violet-900/50'
              : 'border-neutral-200 bg-white text-neutral-600 hover:bg-neutral-50 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-300 dark:hover:bg-neutral-800',
          ].join(' ')}
        >
          {phase === 'loading' ? (
            <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
          ) : (
            <Sparkles className="h-3 w-3" aria-hidden />
          )}
          {buttonLabel}
        </button>

        {phase === 'loading' && (
          // The local model is co-located on the CPU-bound backend box, so a
          // parse can take ~10-60s under load — set the expectation so the
          // spinner doesn't read as "stuck" (FEAT-2026-06-17-KANBAN-FOLLOWUP).
          <span className="text-[11px] text-neutral-400 dark:text-neutral-500">
            Reading your note on the local model — this can take a moment.
          </span>
        )}
        {phase === 'done' && unavailable && (
          <span className="text-[11px] text-neutral-400 dark:text-neutral-500">
            AI structuring is offline — use the quick suggestions above.
          </span>
        )}
        {phase === 'done' && !unavailable && pending.length === 0 && (
          <span className="text-[11px] text-neutral-400 dark:text-neutral-500">
            No actions found in this note.
          </span>
        )}
      </div>

      {pending.length > 0 && (
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          {pending.map(({ action, idx }) => {
            const { userId, hasEffect } = resolveEffect(action, members)
            const busy = busyIdx === idx
            const personLabel = action.person_name
              ? userId != null
                ? `· ${action.person_name}`
                : `· ${action.person_name}?`
              : ''
            const dateLabel = action.date ? `· ${action.date}` : ''
            const title =
              action.title.length > 36 ? `${action.title.slice(0, 36)}…` : action.title
            return (
              <span
                key={`${idx}:${action.title}`}
                className="group inline-flex items-center overflow-hidden rounded-full border border-violet-200 bg-violet-50 text-[11px] text-violet-700 dark:border-violet-900/60 dark:bg-violet-950/40 dark:text-violet-300"
              >
                <button
                  type="button"
                  onClick={() => void applyAction(action, idx)}
                  disabled={busyIdx != null || !hasEffect}
                  title={hasEffect ? 'Apply this action' : 'Nothing to apply'}
                  className="inline-flex items-center gap-1 py-0.5 pl-2 pr-1.5 font-medium hover:bg-violet-100 disabled:opacity-60 dark:hover:bg-violet-900/50"
                >
                  {busy ? (
                    <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                  ) : userId != null ? (
                    <UserPlus className="h-3 w-3" aria-hidden />
                  ) : (
                    KIND_ICON[action.kind]
                  )}
                  <span className="truncate max-w-[16rem]">
                    {title}
                    {(personLabel || dateLabel) && (
                      <span className="opacity-70">
                        {' '}
                        {personLabel} {dateLabel}
                      </span>
                    )}
                  </span>
                  {!busy && hasEffect && (
                    <Check
                      className="h-3 w-3 opacity-50 transition-opacity group-hover:opacity-90"
                      aria-hidden
                    />
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => markApplied(idx)}
                  disabled={busyIdx != null}
                  aria-label={`Dismiss ${action.title}`}
                  title="Dismiss"
                  className="border-l border-violet-200 px-1 py-0.5 text-violet-400 hover:bg-violet-100 hover:text-violet-700 dark:border-violet-900/60 dark:hover:bg-violet-900/50"
                >
                  <X className="h-3 w-3" aria-hidden />
                </button>
              </span>
            )
          })}
        </div>
      )}
    </div>
  )
}
